import React, { useContext, useMemo } from "react";
import { Link as RouterLink, NavLink, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import AuthContext from "../auth/AuthContext";
import AuthAvatar from "./AuthAvatar";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { getSettings } from "../utils/apiClient";

const navLinkSx = {
  textTransform: "none",
  color: "text.secondary",
  fontWeight: 600,
  minWidth: 0,
  px: { xs: 1, sm: 1.5 },
  "&.active": {
    color: "text.primary",
  },
};

const drawerLinkSx = {
  "&.active": {
    bgcolor: "action.selected",
    color: "text.primary",
    fontWeight: 600,
  },
};

function AppTopNav() {
  const theme = useTheme();
  const isMobileNav = useMediaQuery(theme.breakpoints.down("md"));
  const { auth, user, logout } = useContext(AuthContext);
  const { siteIdentity } = useSiteSettings();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [toolsAnchorEl, setToolsAnchorEl] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [visibleTopics, setVisibleTopics] = React.useState(["home", "categories", "recipes"]);
  const signedIn = !!auth?.user;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSettings();
        if (cancelled) return;
        if (Array.isArray(data?.visible_topics)) {
          setVisibleTopics(data.visible_topics);
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.user_uuid, user?.is_admin]);

  const navItems = useMemo(() => {
    const items = [{ to: "/", label: "Hjem", end: true }];
    if (visibleTopics.includes("categories")) {
      items.push({ to: "/categories", label: "Kategorier" });
    }
    if (visibleTopics.includes("recipes")) {
      items.push({ to: "/recipes", label: "Opskrifter" });
    }
    return items;
  }, [visibleTopics]);

  const toolItems = useMemo(() => {
    const items = [];
    if (signedIn) {
      items.push({ to: "/tools/wine-manager", label: "Vin manager" });
      items.push({ to: "/wine-tasting", label: "Vinsmagning" });
    }
    if (user?.is_admin) {
      items.push({ to: "/grounding", label: "Refleksion-journal" });
      items.push({ to: "/finance", label: "Økonomi" });
    }
    return items;
  }, [signedIn, user?.is_admin]);

  const showTools = toolItems.length > 0;

  const openUserMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeUserMenu = () => {
    setAnchorEl(null);
  };

  const openToolsMenu = (event) => {
    setToolsAnchorEl(event.currentTarget);
  };

  const closeToolsMenu = () => {
    setToolsAnchorEl(null);
  };

  const navigateAndClose = (to) => {
    closeUserMenu();
    closeToolsMenu();
    navigate(to);
  };

  const signOutAndClose = () => {
    closeUserMenu();
    logout();
    navigate("/");
  };

  const closeMobileNav = () => {
    setMobileOpen(false);
  };

  const navigateMobile = (to) => {
    closeMobileNav();
    navigate(to);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{
          bgcolor: "#ffffff",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ gap: 1, maxWidth: 960, width: "100%", mx: "auto", px: { xs: 2, sm: 3 } }}>
          {isMobileNav ? (
            <IconButton
              edge="start"
              aria-label="Menu"
              onClick={() => setMobileOpen(true)}
              sx={{ ml: -0.5, mr: 0.25 }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}

          <Typography
            component={RouterLink}
            to="/"
            variant="h6"
            sx={{
              textDecoration: "none",
              color: "text.primary",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              fontSize: { xs: "1.15rem", sm: "1.35rem" },
              mr: { xs: 0.5, sm: 1 },
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {siteIdentity}
          </Typography>

          {!isMobileNav ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
              {navItems.map((item) => (
                <Button
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.end}
                  sx={navLinkSx}
                >
                  {item.label}
                </Button>
              ))}
              {showTools ? (
                <>
                  <Button
                    onClick={openToolsMenu}
                    endIcon={<ExpandMoreIcon />}
                    sx={navLinkSx}
                    aria-haspopup="true"
                    aria-expanded={Boolean(toolsAnchorEl) ? "true" : undefined}
                  >
                    Værktøjer
                  </Button>
                  <Menu
                    anchorEl={toolsAnchorEl}
                    open={Boolean(toolsAnchorEl)}
                    onClose={closeToolsMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {toolItems.map((item) => (
                      <MenuItem key={item.to} onClick={() => navigateAndClose(item.to)}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ flexGrow: 1 }} />

          {signedIn ? (
            <>
              <IconButton
                onClick={openUserMenu}
                title="Account"
                aria-label="Account menu"
                sx={{ p: 0.25 }}
              >
                {user?.avatar_url ? (
                  <AuthAvatar
                    key={user.avatar_url}
                    src={user.avatar_url}
                    alt={user?.username || "Profile"}
                    sx={{ width: 44, height: 44 }}
                  >
                    {(user?.username || "?").slice(0, 1).toUpperCase()}
                  </AuthAvatar>
                ) : (
                  <AccountCircleIcon sx={{ color: "text.primary", fontSize: 44 }} />
                )}
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeUserMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => navigateAndClose("/profile")}>Profil</MenuItem>
                <MenuItem onClick={signOutAndClose}>Log ud</MenuItem>
              </Menu>
            </>
          ) : (
            <IconButton
              onClick={() => navigate("/login")}
              title="Log ind"
              aria-label="Log ind"
              sx={{ color: "text.primary", p: 0.25 }}
            >
              <AccountCircleIcon sx={{ fontSize: 44 }} />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={closeMobileNav}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        <Box sx={{ width: 280, pt: 1 }} role="presentation">
          <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
            Menu
          </Typography>
          <List disablePadding>
            {navItems.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                end={item.end}
                onClick={closeMobileNav}
                sx={drawerLinkSx}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
          {showTools ? (
            <List
              disablePadding
              subheader={
                <ListSubheader component="div" sx={{ bgcolor: "transparent", lineHeight: "36px" }}>
                  Værktøjer
                </ListSubheader>
              }
            >
              {toolItems.map((item) => (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  onClick={closeMobileNav}
                  sx={drawerLinkSx}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          ) : null}
          <List disablePadding sx={{ borderTop: 1, borderColor: "divider", mt: 1 }}>
            {signedIn ? (
              <>
                <ListItemButton onClick={() => navigateMobile("/profile")}>
                  <ListItemText primary="Profil" />
                </ListItemButton>
                <ListItemButton
                  onClick={() => {
                    closeMobileNav();
                    signOutAndClose();
                  }}
                >
                  <ListItemText primary="Log ud" />
                </ListItemButton>
              </>
            ) : (
              <ListItemButton onClick={() => navigateMobile("/login")}>
                <ListItemText primary="Log ind" />
              </ListItemButton>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default AppTopNav;
